import React from 'react';
import {
  Dialog,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Avatar,
} from '@mui/material';
import { PictureAsPdf, KeyboardArrowRight, PhoneIphone, DeleteOutline } from '@mui/icons-material';
import contractorService, { Contractor } from '../../services/contractorService';
import { getAllProjects, getInitials } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { createOtherExpense, updateOtherExpense, deleteOtherExpense, type OtherExpenseDto } from '../../services/otherExpensesService';
import { useNotification } from '../layouts/Layout';
import { getSocket } from '../../services/realtimeService';
import api from '../../services/api';

export interface OtherExpenseFormData {
  expenseDate: string;
  receivedDate: string;
  projectId: string;
  description: string;
  warehouse: string;
  contractorName: string;
  bankAccount: string;
  contractorId?: string;
  nip: string;
  contractorAddress: string;
  contractorPostalCity: string;
  documentNumber: string;
  amount: number | '';
  dueDate: string;
  priority: 'zwykły' | 'pilny';
  paymentMethod: 'Przelew' | 'Gotówka' | 'Karta';
  file?: File | null;
  legalAcceptance: boolean;
  comment: string;
}

interface AddOtherExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data?: OtherExpenseFormData) => void;
  /** Tryb edycji – przekaż dane wydatku do edycji */
  initialData?: OtherExpenseDto | null;
}

const STATIC_PROJECTS: Array<{ project_id: string; project_name: string }> = [
  { project_id: 'S14', project_name: 'SERWIS GWARANCYJNY: USŁUGI' },
  { project_id: 'S13', project_name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
  { project_id: 'S12', project_name: 'LEASINGI' },
  { project_id: 'S11', project_name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
  { project_id: 'S10', project_name: 'USŁUGI TELEINFORMATYCZNE' },
  { project_id: 'S9', project_name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
  { project_id: 'S8', project_name: 'NAJEM POWIERZCHNI BIUROWEJ' },
  { project_id: 'S7', project_name: 'PRACOWNICY B2B' },
  { project_id: 'S6', project_name: 'ZARZĄD' },
  { project_id: 'S5', project_name: 'USŁUGI WŁASNE' },
  { project_id: 'S4', project_name: 'MATERIAŁY WŁASNE' },
  { project_id: 'S3', project_name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
  { project_id: 'S2', project_name: 'WYPOSAŻENIE - do 3.000 zł' },
  { project_id: 'S1', project_name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
  { project_id: 'R11', project_name: 'PODATEK CIT' },
  { project_id: 'R10', project_name: 'PODATEK VAT' },
  { project_id: 'R9', project_name: 'ZUS' },
  { project_id: 'R8', project_name: 'PODATKI - PIT' },
  { project_id: 'R7', project_name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
  { project_id: 'R6', project_name: 'OBSŁUGA BANKOWA' },
  { project_id: 'R5', project_name: 'UBEZPIECZENIA I POLISY' },
  { project_id: 'R4', project_name: 'PRACOWNICY UoP, UD, UZ' },
  { project_id: 'R3', project_name: 'ZABEZPIECZENIE KONTRAKTÓW' },
  { project_id: 'R2', project_name: 'KOSZTY KREDYTÓW' },
  { project_id: 'R1', project_name: 'AMORTYZACJA' },
  { project_id: '.UN', project_name: 'USŁUGI NOCLEGOWE' },
  { project_id: '.UG', project_name: 'USŁUGI GASTRONOMICZNE' },
  { project_id: '.P2', project_name: 'POJAZDY - 50% ODLICZENIA VAT' },
  { project_id: '.P1', project_name: 'POJAZDY - 100% ODLICZENIA VAT' },
];

const AddOtherExpenseDialog: React.FC<AddOtherExpenseDialogProps> = ({ open, onClose, onSave, initialData }) => {
  const editMode = Boolean(initialData?.id);
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [projects, setProjects] = React.useState<Array<{ project_id: string; project_name: string }>>([]);
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  const [contractorSearchQuery, setContractorSearchQuery] = React.useState('');
  const [contractorOptions, setContractorOptions] = React.useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = React.useState(false);

  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [showErrors, setShowErrors] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [form, setForm] = React.useState<OtherExpenseFormData>({
    expenseDate: '',
    receivedDate: '',
    projectId: '',
    description: '',
    warehouse: '',
    contractorName: '',
    bankAccount: '',
    contractorId: undefined,
    nip: '',
    contractorAddress: '',
    contractorPostalCity: '',
    documentNumber: '',
    amount: '',
    dueDate: '',
    priority: 'zwykły',
    paymentMethod: 'Przelew',
    file: null,
    legalAcceptance: false,
    comment: '',
  });

  const [scanWaiting, setScanWaiting] = React.useState(false);
  const [scanSessionId, setScanSessionId] = React.useState<string | null>(null);
  const scanHandlerRef = React.useRef<((p: any) => void) | null>(null);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': { height: 32, backgroundColor: '#fff', borderRadius: '4px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': { padding: '8px 12px', boxSizing: 'border-box', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
    '& .MuiFormHelperText-root': { ml: 0 },
    '& .MuiInputLabel-root': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '12px', color: '#2D2D32' }
  } as const;

  const selectSx = {
    height: 32,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': { padding: '8px 12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
    backgroundColor: '#fff',
    borderRadius: '4px',
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  React.useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        setLoadingProjects(true);
        const all = await getAllProjects();
        const active = all.filter((p: any) => p.project_phase === 'W REALIZACJI')
          .map((p: any) => ({ project_id: p.project_id, project_name: p.project_name }));
        setProjects([...active, ...STATIC_PROJECTS]);
      } finally {
        setLoadingProjects(false);
      }
    };
    load();
  }, [open]);

  React.useEffect(() => {
    if (open && initialData) {
      setForm({
        expenseDate: initialData.expense_date?.slice(0, 10) || '',
        receivedDate: initialData.received_date?.slice(0, 10) || '',
        projectId: initialData.project_id || '',
        description: initialData.description || '',
        warehouse: initialData.warehouse || '',
        contractorName: initialData.contractor_name || '',
        bankAccount: initialData.account_number || '',
        contractorId: initialData.contractor_id || undefined,
        nip: initialData.nip || '',
        contractorAddress: initialData.contractor_address || '',
        contractorPostalCity: initialData.contractor_postal_city || '',
        documentNumber: initialData.document_number || '',
        amount: initialData.gross_amount ?? '',
        dueDate: initialData.due_date?.slice(0, 10) || '',
        priority: (initialData.priority === 'pilny' ? 'pilny' : 'zwykły') as 'zwykły' | 'pilny',
        paymentMethod: (initialData.payment_method === 'Gotówka' ? 'Gotówka' : initialData.payment_method === 'Karta' ? 'Karta' : 'Przelew') as 'Przelew' | 'Gotówka' | 'Karta',
        file: null,
        legalAcceptance: true,
        comment: initialData.comment || '',
      });
      setContractorSearchQuery(initialData.contractor_name || '');
      if (initialData.google_drive_file_id && initialData.id) {
        setFileUrl(null);
        api.get(`/other-expenses/${initialData.id}/pdf`, { responseType: 'blob' })
          .then((resp) => {
            const blob = resp.data as Blob;
            const objUrl = URL.createObjectURL(blob);
            setFileUrl(objUrl);
          })
          .catch(() => setFileUrl(null));
      } else {
        setFileUrl(null);
      }
    }
  }, [open, initialData?.id]);

  const searchContractors = React.useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setContractorOptions([]);
      return;
    }
    try {
      setLoadingContractors(true);
      const response = await contractorService.filterContractors({ searchText: query }, 1, 10);
      setContractorOptions(response.contractors);
    } catch {
      setContractorOptions([]);
    } finally {
      setLoadingContractors(false);
    }
  }, []);

  const onDropFile = (f: File) => {
    if (f && f.type === 'application/pdf') {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
      const url = URL.createObjectURL(f);
      setFileUrl(url);
      setForm((prev) => ({ ...prev, file: f }));
    }
  };

  const canSave =
    !!form.expenseDate &&
    !!form.projectId &&
    !!form.description &&
    (typeof form.amount === 'number' && form.amount > 0) &&
    !!form.legalAcceptance;

  const resetState = () => {
    setForm({
      expenseDate: '',
      receivedDate: '',
      projectId: '',
      description: '',
      warehouse: '',
      contractorName: '',
      bankAccount: '',
      contractorId: undefined,
      nip: '',
      contractorAddress: '',
      contractorPostalCity: '',
      documentNumber: '',
      amount: '',
      dueDate: '',
      priority: 'zwykły',
      paymentMethod: 'Przelew',
      file: null,
    legalAcceptance: false,
    comment: '',
    });
    if (fileUrl && fileUrl.startsWith('blob:')) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setContractorSearchQuery('');
    setContractorOptions([]);
    setShowErrors(false);
    try {
      const s = getSocket();
      const h = scanHandlerRef.current;
      if (h) s.off('scan:completed', h);
    } catch {}
    scanHandlerRef.current = null;
    setScanWaiting(false);
    setScanSessionId(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSave = () => {
    if (saving) return;
    if (!canSave) { setShowErrors(true); return; }
    const amount = typeof form.amount === 'number' ? form.amount : 0;
    const payload = {
      expense_date: form.expenseDate,
      received_date: form.receivedDate || undefined,
      project_id: form.projectId,
      description: form.description,
      warehouse: form.warehouse || undefined,
      contractor_name: form.contractorName || undefined,
      account_number: form.bankAccount || undefined,
      contractor_id: form.contractorId || undefined,
      nip: form.nip || undefined,
      contractor_address: form.contractorAddress || undefined,
      contractor_postal_city: form.contractorPostalCity || undefined,
      document_number: form.documentNumber || undefined,
      net_amount: amount,
      vat_rate: '0',
      gross_amount: amount,
      due_date: form.dueDate || undefined,
      priority: form.priority,
      payment_method: form.paymentMethod,
      comment: form.comment || undefined,
      pdf: form.file || undefined,
    };
    showNotification(editMode ? 'Trwa zapisywanie zmian...' : 'Trwa zapisywanie wydatku...', 'info');
    setSaving(true);
    const savePromise = editMode && initialData?.id
      ? updateOtherExpense(initialData.id, payload)
      : createOtherExpense(payload, (user as any)?.google_access_token);
    savePromise
      .then(() => {
        showNotification(editMode ? 'Wydatek został zaktualizowany.' : 'Wydatek został dodany.', 'success');
        onSave?.(form);
        handleClose();
      })
      .catch((e: any) => {
        console.error('Nie udało się zapisać wydatku:', e);
        showNotification(e?.response?.data?.error || 'Nie udało się zapisać wydatku.', 'error');
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = async () => {
    if (!editMode || !initialData?.id) return;
    if (!window.confirm('Czy na pewno chcesz usunąć ten wydatek?')) return;
    try {
      await deleteOtherExpense(initialData.id);
      showNotification('Wydatek został usunięty.', 'success');
      onSave?.();
      handleClose();
    } catch (e: any) {
      showNotification(e?.response?.data?.error || 'Nie udało się usunąć wydatku.', 'error');
    }
  };

  const startPhoneScan = async () => {
    if (scanWaiting) return;
    setScanWaiting(true);
    try {
      const r = await api.post('/scan-requests', { type: 'other-expense' });
      const sid = r?.data?.data?.sessionId || null;
      if (!sid) throw new Error('Nie udało się utworzyć żądania skanu.');
      setScanSessionId(sid);
      const s = getSocket();
      const handler = async (payload: any) => {
        try {
          if (!payload || payload.sessionId !== sid || !payload.url) return;
          const baseURL = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
          const origin = baseURL.replace(/\/?api\/?$/, '');
          const absUrl = payload.url.startsWith('http') ? payload.url : `${origin}${payload.url}`;
          const resp = await fetch(absUrl);
          const blob = await resp.blob();
          const f = new File([blob], `scan-${Date.now()}.pdf`, { type: 'application/pdf' });
          onDropFile(f);
        } catch {}
        try { s.off('scan:completed', handler); } catch {}
        scanHandlerRef.current = null;
        setScanWaiting(false);
        setScanSessionId(null);
      };
      scanHandlerRef.current = handler;
      s.on('scan:completed', handler);
    } catch {
      setScanWaiting(false);
      setScanSessionId(null);
      showNotification('Nie udało się wysłać prośby o skan.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { bgcolor: '#FAFAFA' } }}>
      {/* Górny pasek */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #EDEDED', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <Tooltip title={user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'Użytkownik'}>
            <Avatar src={user?.avatar_url || undefined} sx={{ width: 28, height: 28, flexShrink: 0 }}>
              {getInitials(user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'U')}
            </Avatar>
          </Tooltip>
          <KeyboardArrowRight sx={{ color: 'text.secondary', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#020242', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {editMode ? 'Edytuj wydatek' : 'Dodaj inny wydatek'}
          </Typography>
        </Box>
      </Box>

      {/* Główna zawartość – podział 50/50 */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', p: 2, gap: 2 }}>
        {/* Lewa kolumna – podgląd PDF (50%) */}
        <Box sx={{ flex: '0 0 50%', minWidth: 0 }}>
          <Box
            sx={{
              border: '2px dashed #d4d4d9',
              borderRadius: '4px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: fileUrl ? '#fff' : 'transparent',
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onDropFile(f);
            }}
          >
            {!fileUrl ? (
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <PictureAsPdf sx={{ fontSize: 56, color: 'text.disabled' }} />
                <Typography sx={{ mt: 1, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>Przeciągnij i upuść plik PDF tutaj</Typography>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#B9B9B9' }}>lub</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" component="label" size="small"
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', borderColor: '#d4d4d9', color: '#2D2D32' }}>
                    Wybierz plik PDF
                    <input type="file" hidden accept="application/pdf" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onDropFile(f);
                    }} />
                  </Button>
                  <Button variant="outlined" onClick={startPhoneScan} startIcon={<PhoneIphone />} disabled={scanWaiting} size="small"
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', borderColor: '#d4d4d9', color: '#2D2D32' }}>
                    {scanWaiting ? 'Oczekiwanie…' : 'Skan z telefonu'}
                  </Button>
                </Box>
                {scanWaiting && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>Sprawdź powiadomienie w aplikacji mobilnej</Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <iframe title="expense-preview" src={fileUrl} style={{ width: '100%', height: '100%', border: 0 }} />
            )}
          </Box>
        </Box>

        {/* Prawa kolumna – formularz (50%) */}
        <Box sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: '4px', border: '1px solid #EDEDED', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', pt: 2, px: 2 }}>
            <Grid container rowSpacing={1.5} columnSpacing={2}>
              <Grid item xs={6}>
                <FieldLabel text="Data wydatku" required />
                <TextField
                  fullWidth type="date" value={form.expenseDate}
                  onChange={(e) => setForm((p) => ({ ...p, expenseDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                  error={showErrors && !form.expenseDate}
                  helperText={showErrors && !form.expenseDate ? 'Wymagane' : ''}
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel text="Data otrzymania" />
                <TextField
                  fullWidth type="date" value={form.receivedDate}
                  onChange={(e) => setForm((p) => ({ ...p, receivedDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={8.4}>
                <FieldLabel text="Projekt" required />
                <Autocomplete
                  size="small"
                  options={projects}
                  loading={loadingProjects}
                  getOptionLabel={(p) => `${p.project_id} ${p.project_name}`}
                  value={projects.find((p) => p.project_id === form.projectId) || null}
                  onChange={(_, val) => setForm((prev) => ({ ...prev, projectId: val ? String(val.project_id) : '' }))}
                  isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
                  ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '12px', py: 0.5, minHeight: 28 } } }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Wybierz projekt" sx={textFieldSx}
                      error={showErrors && !form.projectId}
                      helperText={showErrors && !form.projectId ? 'Wymagane' : ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3.6}>
                <FieldLabel text="Magazyn" />
                <TextField fullWidth size="small" value={form.warehouse || ''} placeholder="000"
                  onChange={(e) => { const v = e.target.value.slice(0, 3).toUpperCase(); setForm((p) => ({ ...p, warehouse: v })); }}
                  inputProps={{ maxLength: 3 }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Opis wydatku" required />
                <TextField
                  fullWidth value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  size="small" sx={textFieldSx}
                  error={showErrors && !form.description}
                  helperText={showErrors && !form.description ? 'Wymagane' : ''}
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel text="Nr dokumentu" />
                <TextField fullWidth value={form.documentNumber}
                  onChange={(e) => setForm((p) => ({ ...p, documentNumber: e.target.value }))}
                  size="small" sx={textFieldSx} placeholder="Nr rachunku / paragonu"
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel text="Termin płatności" />
                <TextField
                  fullWidth type="date" value={form.dueDate}
                  onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel text="Wartość (brutto)" required />
                <TextField
                  fullWidth type="number" value={form.amount}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                    setForm((p) => ({ ...p, amount: v as any }));
                  }}
                  size="small" sx={textFieldSx}
                  inputProps={{ step: '0.01', min: 0 }}
                  placeholder="0.00"
                  error={showErrors && (form.amount === '' || form.amount === 0)}
                  helperText={showErrors && (form.amount === '' || form.amount === 0) ? 'Wymagane' : ''}
                />
              </Grid>
              <Grid item xs={3}>
                <FieldLabel text="Priorytet" />
                <Select
                  fullWidth size="small" value={form.priority}
                  onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value as any }))}
                  sx={selectSx}
                >
                  <MenuItem value="zwykły">Zwykły</MenuItem>
                  <MenuItem value="pilny">Pilny</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={3}>
                <FieldLabel text="Płatność" />
                <Select
                  fullWidth size="small" value={form.paymentMethod}
                  onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value as any }))}
                  sx={selectSx}
                >
                  <MenuItem value="Przelew">Przelew</MenuItem>
                  <MenuItem value="Gotówka">Gotówka</MenuItem>
                  <MenuItem value="Karta">Karta</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={7.8}>
                <FieldLabel text="Kontrahent" />
                <Autocomplete
                  size="small" freeSolo options={contractorOptions} loading={loadingContractors}
                  inputValue={contractorSearchQuery}
                  onInputChange={(_, newValue, reason) => {
                    setContractorSearchQuery(newValue);
                    if (reason === 'input') searchContractors(newValue);
                  }}
                  value={null}
                  onChange={(_, newValue) => {
                    if (typeof newValue === 'string') {
                      setForm((prev) => ({ ...prev, contractorId: undefined, contractorName: newValue.trim(), nip: '', contractorAddress: '', contractorPostalCity: '' }));
                      setContractorSearchQuery(newValue.trim());
                    } else if (newValue && typeof newValue === 'object' && 'id' in newValue) {
                      const c = newValue as Contractor;
                      setForm((prev) => ({ ...prev, contractorId: String(c.id), contractorName: c.name, nip: c.nip || '', contractorAddress: c.address || '', contractorPostalCity: c.location || '' }));
                      setContractorSearchQuery(c.name);
                    } else {
                      setForm((prev) => ({ ...prev, contractorId: undefined, contractorName: '', nip: '', contractorAddress: '', contractorPostalCity: '' }));
                      setContractorSearchQuery('');
                    }
                  }}
                  getOptionLabel={(o) => typeof o === 'string' ? o : (o as Contractor).name || ''}
                  renderOption={(props, option) => {
                    const c = option as Contractor;
                    return (
                      <li {...props} key={c.id}>
                        <Box>
                          <Typography sx={{ fontSize: '13px', fontFamily: 'Roboto, sans-serif' }}>{c.name}</Typography>
                          {c.nip && <Typography sx={{ fontSize: '11px', color: '#999' }}>NIP: {c.nip}</Typography>}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Szukaj kontrahenta..." sx={textFieldSx} />
                  )}
                />
              </Grid>
              <Grid item xs={4.2}>
                <FieldLabel text="NIP" />
                <TextField fullWidth value={form.nip}
                  onChange={(e) => setForm((p) => ({ ...p, nip: e.target.value }))}
                  size="small" sx={textFieldSx} placeholder="NIP kontrahenta"
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel text="Adres (ulica i nr)" />
                <TextField fullWidth value={form.contractorAddress || ''}
                  onChange={(e) => setForm((p) => ({ ...p, contractorAddress: e.target.value }))}
                  size="small" sx={textFieldSx} placeholder="ul. Przykładowa 1"
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel text="Kod pocztowy i miasto" />
                <TextField fullWidth value={form.contractorPostalCity || ''}
                  onChange={(e) => setForm((p) => ({ ...p, contractorPostalCity: e.target.value }))}
                  size="small" sx={textFieldSx} placeholder="00-000 Warszawa"
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel text="Nr rachunku kontrahenta" />
                <TextField fullWidth value={form.bankAccount || ''}
                  onChange={(e) => setForm((p) => ({ ...p, bankAccount: e.target.value }))}
                  size="small" sx={textFieldSx} placeholder="Nr rachunku bankowego"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Dolny pasek z przyciskami */}
          <Box sx={{ borderTop: '1px solid #EDEDED', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {editMode && (
                <Button onClick={handleDelete} startIcon={<DeleteOutline />} color="error" size="small"
                  sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', whiteSpace: 'nowrap' }}>
                  Usuń
                </Button>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.legalAcceptance}
                    onChange={(_, checked) => setForm((p) => ({ ...p, legalAcceptance: checked }))}
                    sx={{ '&.Mui-checked': { color: '#4040DB' } }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: showErrors && !form.legalAcceptance ? '#d32f2f' : '#595966' }}>
                    Potwierdzam prawidłowość danych
                  </Typography>
                }
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined" onClick={handleClose}
                sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', borderColor: '#d4d4d9', color: '#2D2D32', '&:hover': { borderColor: '#595966' } }}
              >
                Anuluj
              </Button>
              <Button
                variant="contained" onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{
                  textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500,
                  backgroundColor: '#4040DB', color: '#fff',
                  '&:hover': { backgroundColor: '#1F1FB0' },
                  '&:disabled': { backgroundColor: '#B0B0F5', color: '#fff' }
                }}
              >
                {saving ? 'Zapisywanie...' : (editMode ? 'Zapisz zmiany' : 'Zapisz wydatek')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddOtherExpenseDialog;
